import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, InputNumber, Select, Switch, Tag, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { KcConfig, KcConfigFlag, KcHomePosition } from '../types';
import {
  ANALYSIS_FLAG_OPTIONS,
  CONFIG_FLAGS,
  HOME_POSITION_OPTIONS,
  MAX_MILESTONE,
  MAX_MILESTONES,
  MIN_MILESTONE,
  honorMinOf,
  kcErrorMessage,
  milestoneInputErrors,
  milestoneLabel,
  milestoneListError,
  normalizeConfig,
  sameMilestones,
  sortedMilestones,
} from '../utils';
import styles from '../style.module.scss';

/** Bản đang sửa: như cấu hình, nhưng ô mốc có thể đang trống (null). */
type Draft = Omit<KcConfig, 'milestones'> & { milestones: (number | null)[] };

const toDraft = (config: KcConfig): Draft => ({
  ...config,
  milestones: config.milestones.slice(),
});

// Ô số có dấu chấm ngăn nghìn (1.000.000). Dấu thập phân đổi sang "," để "." không bị hiểu là
// phần lẻ khi gõ.
const formatThousands = (value?: number | string) =>
  value === undefined || value === null || value === ''
    ? ''
    : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const parseThousands = (value?: string) => (value || '').replace(/[^\d]/g, '');

interface Props {
  /** null = chưa tải được cấu hình (ô bị khoá). */
  config: KcConfig | null;
  /** Chỉ ADMIN được sửa (PUT cấu hình trả 403 với MOD). */
  canEdit: boolean;
  /** Lưu xong: `milestonesChanged` = máy chủ đã tính lại tháng hiện tại theo mốc mới. */
  onSaved: (config: KcConfig, milestonesChanged: boolean) => void;
}

/**
 * Khối cấu hình đầu trang: 2 công tắc + vị trí trên Trang chủ + phần nào của câu phân tích hiện
 * trên app + danh sách mốc + mốc bắt đầu vinh danh (docs/chuc-mung-moc-kc/DAC-TA-2026-10-01.md
 * §4.1, §6). MOD xem được nhưng mọi ô bị khoá.
 */
const ConfigCard = ({ config, canEdit, onSaved }: Props) => {
  const { message } = App.useApp();
  const [draft, setDraft] = useState<Draft | null>(config ? toDraft(config) : null);
  const [saving, setSaving] = useState(false);
  // Ô vừa thêm được đặt con trỏ vào.
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  // Cấu hình trên máy chủ đổi (tải trang, đổi tháng, lưu xong) → đặt lại bản đang sửa. Đổi tháng
  // mà cấu hình không đổi thì khoá giống hệt → giữ nguyên phần đang sửa dở.
  const configKey = config ? JSON.stringify(config) : '';
  useEffect(() => {
    setDraft(config ? toDraft(config) : null);
    setFocusIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  const itemErrors = useMemo(
    () => (draft ? milestoneInputErrors(draft.milestones) : []),
    [draft]
  );
  const listError = draft ? milestoneListError(draft.milestones) : null;
  const invalid = !!listError || itemErrors.some(Boolean);

  const milestonesChanged =
    !!draft && !!config && !sameMilestones(sortedMilestones(draft.milestones), config.milestones);

  // Các mốc hợp lệ đang sửa (tăng dần) → lựa chọn của "Mốc bắt đầu vinh danh". Mốc đang chọn bị
  // bỏ / sửa số thì tự chuyển sang mốc kế tiếp, đúng như máy chủ (honorMinOf).
  const validMilestones = useMemo(
    () =>
      sortedMilestones(
        (draft?.milestones || []).filter((value, index) => value !== null && !itemErrors[index])
      ),
    [draft, itemErrors]
  );
  const honorMin =
    draft && validMilestones.length ? honorMinOf(validMilestones, draft.honorMin) : null;
  const honorOptions = validMilestones.map((value) => ({
    value,
    label: milestoneLabel(value),
  }));

  const dirty =
    !!draft &&
    !!config &&
    (CONFIG_FLAGS.some((key) => draft[key] !== config[key]) ||
      draft.homePosition !== config.homePosition ||
      draft.honorMin !== config.honorMin ||
      draft.milestones.length !== config.milestones.length ||
      draft.milestones.some((value, index) => value !== config.milestones[index]));

  const disabled = !canEdit || !draft || saving;

  const setFlag = (key: KcConfigFlag, checked: boolean) =>
    setDraft((prev) => (prev ? { ...prev, [key]: checked } : prev));

  const flagSwitch = (key: KcConfigFlag, label: string) => (
    <label key={key} className={styles.switchItem}>
      <Switch checked={!!draft?.[key]} disabled={disabled} onChange={(checked) => setFlag(key, checked)} />
      {label}
    </label>
  );

  const updateMilestone = (index: number, value: number | null) =>
    setDraft((prev) =>
      prev
        ? { ...prev, milestones: prev.milestones.map((item, i) => (i === index ? value : item)) }
        : prev
    );

  const removeMilestone = (index: number) =>
    setDraft((prev) =>
      prev ? { ...prev, milestones: prev.milestones.filter((_, i) => i !== index) } : prev
    );

  const addMilestone = () => {
    if (!draft || draft.milestones.length >= MAX_MILESTONES) return;
    setFocusIndex(draft.milestones.length);
    setDraft({ ...draft, milestones: [...draft.milestones, null] });
  };

  const save = async () => {
    if (!draft || !config || invalid || !dirty) return;
    // Chỉ gửi phần đã đổi: gửi lại danh sách mốc cũ sẽ làm máy chủ tính lại tháng không cần thiết.
    const body: Partial<KcConfig> = {};
    CONFIG_FLAGS.forEach((key) => {
      if (draft[key] !== config[key]) body[key] = draft[key];
    });
    if (draft.homePosition !== config.homePosition) body.homePosition = draft.homePosition;
    if (milestonesChanged) body.milestones = sortedMilestones(draft.milestones);
    // Chỉ gửi khi Admin chọn lại. Đổi danh sách mốc mà không chọn: máy chủ tự dời sang mốc kế tiếp
    // giống ô chọn đang hiện (honorMinOf).
    if (honorMin !== null && draft.honorMin !== config.honorMin) body.honorMin = honorMin;
    if (Object.keys(body).length === 0) {
      // Chỉ khác thứ tự gõ, cùng một bộ mốc: không cần gọi máy chủ.
      setDraft(toDraft(config));
      return;
    }
    setSaving(true);
    try {
      const res: any = await AxiosUtils.put(API.kcMilestonesAdminConfig, body);
      const saved = res?.data && typeof res.data === 'object' ? res.data : { ...config, ...body };
      const next = normalizeConfig(saved);
      message.success(
        milestonesChanged
          ? 'Đã lưu cấu hình. Tháng hiện tại đã được tính lại theo mốc mới.'
          : 'Đã lưu cấu hình.'
      );
      onSaved(next, milestonesChanged);
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /** Mốc hợp lệ nằm dưới mốc bắt đầu vinh danh. */
  const isRecordOnly = (value: number | null, index: number) =>
    value !== null && !itemErrors[index] && honorMin !== null && value < honorMin;

  const errors = [
    listError,
    ...itemErrors.map((error, index) => (error ? `Mốc ${index + 1}: ${error}` : null)),
  ].filter((text): text is string => !!text);

  return (
    <section className={styles.config} aria-labelledby="kc-config-title">
      <div className={styles.configHead}>
        <h2 id="kc-config-title" className={styles.configTitle}>
          Cấu hình
        </h2>
        {!canEdit && <span className={styles.readonlyNote}>Chỉ Admin được sửa cấu hình</span>}
      </div>

      <div className={styles.switches}>
        {flagSwitch('homeEnabled', 'Hiện trên Trang chủ app')}
        {flagSwitch('pushEnabled', 'Push chúc mừng cho Creator')}
      </div>

      <label className={styles.fieldLabel} htmlFor="kc-home-position">
        Vị trí trên Trang chủ
      </label>
      <Select<KcHomePosition>
        id="kc-home-position"
        className={styles.positionSelect}
        value={draft?.homePosition}
        options={HOME_POSITION_OPTIONS}
        disabled={disabled}
        onChange={(value) => setDraft((prev) => (prev ? { ...prev, homePosition: value } : prev))}
      />
      <p className={`${styles.help} ${styles.positionHelp}`}>
        App cập nhật vị trí khi người dùng mở lại Trang chủ.
      </p>

      {/* Chỉ ảnh hưởng câu phân tích người xem nội bộ thấy trên app; trang này luôn đủ số. */}
      <div role="group" aria-labelledby="kc-analysis-flags">
        <p id="kc-analysis-flags" className={styles.fieldLabel}>
          Hiện trong phân tích trên app
        </p>
        <div className={`${styles.switches} ${styles.analysisSwitches}`}>
          {ANALYSIS_FLAG_OPTIONS.map((option) => flagSwitch(option.key, option.label))}
        </div>
        <p className={`${styles.help} ${styles.positionHelp}`}>
          Tắt để giữ bí mật doanh thu: app chỉ hiện phần còn bật. Lời chúc mừng cho fan không bao
          giờ có số hay xu hướng.
        </p>
      </div>

      <p className={styles.fieldLabel}>Các mốc Kim cương</p>
      <p className={styles.help}>
        Mốc tính theo tổng Kim cương trong tháng (cộng các file ngày). Mọi mốc đều được ghi
        nhận để phân tích; chỉ từ “Mốc bắt đầu vinh danh” mới được chúc mừng trên app.
      </p>

      <ul className={styles.milestoneList} aria-label="Các mốc Kim cương">
        {(draft?.milestones || []).map((value, index) => (
          // Ô mốc không có khoá ổn định (giá trị đang gõ dở): dùng vị trí.
          <li key={index} className={styles.milestoneItem}>
            <span className={styles.milestoneIndex}>{index + 1}.</span>
            <InputNumber<number>
              className={styles.milestoneInput}
              aria-label={`Mốc ${index + 1} (Kim cương)`}
              value={value}
              min={MIN_MILESTONE}
              max={MAX_MILESTONE}
              step={100000}
              precision={0}
              controls={false}
              decimalSeparator=","
              formatter={formatThousands}
              // Ô trống phải ra null chứ không phải 0: trả chuỗi chữ số như ví dụ của antd.
              parser={(text) => parseThousands(text) as unknown as number}
              status={itemErrors[index] ? 'error' : undefined}
              disabled={disabled}
              autoFocus={focusIndex === index}
              placeholder="vd. 1.000.000"
              onChange={(next) =>
                updateMilestone(index, typeof next === 'number' && next > 0 ? next : null)
              }
            />
            <span
              className={
                isRecordOnly(value, index)
                  ? `${styles.milestonePreview} ${styles.milestoneMuted}`
                  : styles.milestonePreview
              }
              aria-hidden
            >
              {value ? milestoneLabel(value) : ''}
            </span>
            {isRecordOnly(value, index) && (
              <Tooltip title="Dưới mốc bắt đầu vinh danh: chỉ ghi nhận để phân tích, không hiện trên Trang chủ và không push.">
                <Tag className={styles.recordOnlyTag}>chỉ ghi nhận</Tag>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Bỏ mốc này">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={`Bỏ mốc ${index + 1}`}
                  disabled={disabled || (draft?.milestones.length || 0) <= 1}
                  onClick={() => removeMilestone(index)}
                />
              </Tooltip>
            )}
          </li>
        ))}
      </ul>

      {canEdit && errors.length > 0 && (
        <ul className={styles.errors} role="alert">
          {errors.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}

      <label className={styles.fieldLabel} htmlFor="kc-honor-min">
        Mốc bắt đầu vinh danh
      </label>
      <Select<number>
        id="kc-honor-min"
        className={styles.honorSelect}
        value={honorMin ?? undefined}
        options={honorOptions}
        disabled={disabled || honorOptions.length === 0}
        placeholder="Chọn mốc"
        onChange={(value) => setDraft((prev) => (prev ? { ...prev, honorMin: value } : prev))}
      />
      <p className={`${styles.help} ${styles.positionHelp}`}>
        Mốc thấp hơn vẫn được ghi nhận và xem trong phân tích, nhưng không hiện trên Trang chủ và
        không push.
      </p>

      {canEdit && (
        <div className={styles.configActions}>
          <Button
            icon={<PlusOutlined />}
            disabled={disabled || (draft?.milestones.length || 0) >= MAX_MILESTONES}
            onClick={addMilestone}
          >
            Thêm mốc
          </Button>
          <Button type="primary" loading={saving} disabled={!draft || !dirty || invalid} onClick={save}>
            Lưu
          </Button>
          {dirty && (
            <Button disabled={saving} onClick={() => config && setDraft(toDraft(config))}>
              Huỷ thay đổi
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

export default ConfigCard;
